import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { QRCodeSVG } from "qrcode.react";
import { Card } from "../../components/ui/Button";
import { Button } from "../../components/ui/Button";
import { Input, Select, Alert, Tabs } from "../../components/ui/Modal";
import { Table } from "../../components/ui/Table";
import { tradeService } from "../../services/trades";
import { DepositAddress, Transaction } from "../../types";
import { formatUSD, formatDate, truncateAddress } from "../../utils/formatters";
import { statusBadge } from "../../components/ui/Button";
import { NETWORKS } from "../../utils/constants";
import toast from "react-hot-toast";
import { Ico } from "../../components/ui/Icon";

const schema = z.object({
  cryptocurrency: z.string().min(1),
  network: z.string().min(1),
  amount_crypto: z
    .string()
    .refine((v) => !isNaN(Number(v)) && Number(v) > 0, "Enter valid amount"),
  txid: z.string().min(10, "Enter valid transaction ID"),
});
type FormData = z.infer<typeof schema>;

const DEPOSIT_COINS = [
  "BTC",
  "ETH",
  "USDT",
  "XRP",
  "SOL",
  "BNB",
  "ADA",
  "DOGE",
  "DOT",
];

const Deposit: React.FC = () => {
  const [activeTab, setActiveTab] = useState("deposit");
  const [depositAddresses, setDepositAddresses] = useState<
    Record<string, DepositAddress[]>
  >({});
  const [currentAddress, setCurrentAddress] = useState<DepositAddress | null>(
    null,
  );
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      cryptocurrency: "BTC",
      network: "Native",
      amount_crypto: "",
      txid: "",
    },
  });

  const selectedCoin = watch("cryptocurrency");
  const selectedNetwork = watch("network");

  // Get available networks for selected coin, default to ['Native'] if not defined
  const availableNetworks = NETWORKS[selectedCoin] || ["Native"];

  // Debug: Log when networks change
  useEffect(() => {
    console.log("Selected coin:", selectedCoin);
    console.log("Available networks:", availableNetworks);
    console.log("Current network:", selectedNetwork);
  }, [selectedCoin, selectedNetwork, availableNetworks]);

  useEffect(() => {
    loadAllDepositAddresses();
  }, []);

  // Reset network when coin changes if current network isn't available
  useEffect(() => {
    if (
      selectedCoin &&
      selectedNetwork &&
      !availableNetworks.includes(selectedNetwork)
    ) {
      console.log(
        `Network ${selectedNetwork} not available for ${selectedCoin}, resetting to ${availableNetworks[0]}`,
      );
      setValue("network", availableNetworks[0]);
    }
  }, [selectedCoin, availableNetworks, selectedNetwork, setValue]);

  // Load address when coin or network changes
  useEffect(() => {
    if (selectedCoin && selectedNetwork) {
      console.log(`Loading address for ${selectedCoin} (${selectedNetwork})`);
      loadDepositAddress();
    }
  }, [selectedCoin, selectedNetwork]);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadAllDepositAddresses = async () => {
    try {
      const res = await tradeService.getAllDepositAddresses();
      console.log("All deposit addresses:", res.data.deposit_addresses);
      setDepositAddresses(res.data.deposit_addresses || {});
    } catch (error) {
      console.error("Failed to load deposit addresses:", error);
    }
  };

  const loadDepositAddress = async () => {
    setLoadingAddress(true);
    try {
      console.log(`Fetching address for ${selectedCoin} - ${selectedNetwork}`);
      const res = await tradeService.getDepositAddress(
        selectedCoin,
        selectedNetwork,
      );
      console.log("API Response:", res.data);
      setCurrentAddress(res.data.deposit_address || null);

      if (!res.data.deposit_address) {
        console.log(
          `No address found for ${selectedCoin} (${selectedNetwork})`,
        );
      }
    } catch (error) {
      console.error("Failed to load deposit address:", error);
      setCurrentAddress(null);
    } finally {
      setLoadingAddress(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const res = await tradeService.getTransactions({
        type: "deposit",
        per_page: 20,
      });
      setTransactions(res.data.data || []);
    } catch (error) {
      console.error("Failed to load transactions:", error);
    }
  };

  const copyAddress = async () => {
    if (!currentAddress?.address) return;
    await navigator.clipboard.writeText(currentAddress.address);
    setCopied(true);
    toast.success("Address copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await tradeService.submitDeposit({
        cryptocurrency: data.cryptocurrency,
        network: data.network,
        amount_crypto: Number(data.amount_crypto),
        txid: data.txid,
        deposit_address: currentAddress?.address,
      });
      toast.success("Deposit request submitted! Awaiting admin approval.");
      reset({
        cryptocurrency: data.cryptocurrency,
        network: data.network,
        amount_crypto: "",
        txid: "",
      });
      loadTransactions();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to submit deposit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <Tabs
        tabs={[
          { id: "deposit", label: "Deposit", icon: <Ico name="arrow_down" /> },
          { id: "history", label: "History", count: transactions.length },
        ]}
        active={activeTab}
        onChange={setActiveTab}
        className="mb-6"
      />

      {activeTab === "deposit" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: Address + QR */}
          <Card>
            <div className="flex flex-col items-center gap-2 mb-4 pb-3 border-b border-gray-800/40">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Ico name="qr" className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-display font-semibold text-gray-50">
                Deposit Address
              </h3>
            </div>

            <div className="flex flex-col gap-4">
              <Select
                label="Cryptocurrency"
                options={DEPOSIT_COINS.map((c) => ({ value: c, label: c }))}
                {...register("cryptocurrency")}
              />
              <Select
                label="Network"
                options={availableNetworks.map((n: string) => ({
                  value: n,
                  label: n,
                }))}
                {...register("network")}
              />
            </div>

            <div className="mt-5">
              {loadingAddress ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full" />
                </div>
              ) : currentAddress ? (
                <div className="flex flex-col items-center gap-4">
                  {/* QR Code */}
                  <div className="p-4 bg-white rounded-2xl shadow-lg">
                    <QRCodeSVG
                      value={currentAddress.address}
                      size={160}
                      bgColor="white"
                      fgColor="#000"
                      level="M"
                    />
                  </div>

                  {/* Address display */}
                  <div className="w-full">
                    <p className="text-xs text-gray-500 mb-1.5">
                      {selectedCoin} Address ({selectedNetwork})
                    </p>
                    <div className="flex items-center gap-2 bg-gray-800/60 rounded-xl p-3 border border-gray-700/40">
                      <p className="text-primary font-mono text-xs flex-1 break-all">
                        {currentAddress.address}
                      </p>
                      <button
                        onClick={copyAddress}
                        className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-700/60 hover:bg-primary/20 text-gray-400 hover:text-primary transition-all"
                      >
                        {copied ? <Ico name="check" /> : <Ico name="copy" />}
                      </button>
                    </div>
                  </div>

                  {/* Label if exists */}
                  {currentAddress.label && (
                    <div className="w-full text-center">
                      <p className="text-xs text-gray-500">
                        <Ico name="info" className="w-3 h-3 inline mr-1" />
                        {currentAddress.label}
                      </p>
                    </div>
                  )}

                  {/* Warning */}
                  <Alert
                    type="warning"
                    message={`Only send ${selectedCoin} on the ${selectedNetwork} network to this address. Sending other assets will result in permanent loss.`}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center py-8 gap-2 text-center">
                  <Ico name="info" className="w-8 h-8 text-gray-600" />
                  <p className="text-gray-500 text-sm">
                    No deposit address configured for {selectedCoin} (
                    {selectedNetwork})
                  </p>
                  <p className="text-gray-600 text-xs">
                    Contact support for assistance
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Right: Submit deposit */}
          <Card>
            <h3 className="font-display font-semibold text-gray-50 mb-4">
              Submit Deposit
            </h3>

            <Alert
              type="info"
              message="After sending funds, fill in the form below with your transaction details. An admin will verify and credit your account."
            />

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-4 mt-4"
            >
              <Input
                label={`Amount (${selectedCoin})`}
                type="number"
                step="any"
                placeholder="0.001"
                error={errors.amount_crypto?.message}
                {...register("amount_crypto")}
              />
              <Input
                label="Transaction ID (TXID)"
                placeholder="Paste your blockchain transaction hash"
                error={errors.txid?.message}
                hint="Copy the transaction hash from your wallet or exchange"
                {...register("txid")}
              />

              <Button
                type="submit"
                fullWidth
                loading={loading}
                icon={<Ico name="arrow_down" />}
              >
                Submit Deposit Request
              </Button>
            </form>

            <div className="mt-4 p-3 bg-gray-800/30 rounded-xl">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                How it works
              </h4>
              {[
                "Send crypto to the address shown",
                "Copy your transaction hash (TXID)",
                "Fill in the form and submit",
                "Admin verifies and credits your account",
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-2 py-1">
                  <span className="w-5 h-5 bg-primary/20 text-primary text-xs font-bold rounded-full flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-gray-400 text-xs">{step}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      ) : (
        <div>
          <h3 className="font-display font-semibold text-gray-50 mb-4">
            Deposit History
          </h3>
          <Table
            columns={[
              {
                key: "reference",
                label: "Reference",
                render: (r) => (
                  <span className="font-mono text-xs text-gray-400">
                    {r.reference}
                  </span>
                ),
              },
              {
                key: "cryptocurrency",
                label: "Asset",
                render: (r) => (
                  <span className="font-mono font-semibold text-gray-50">
                    {r.cryptocurrency}{" "}
                    <span className="text-gray-500 text-xs">({r.network})</span>
                  </span>
                ),
              },
              {
                key: "amount_crypto",
                label: "Amount",
                render: (r) => (
                  <span className="font-mono text-primary">
                    {r.amount_crypto} {r.cryptocurrency}
                  </span>
                ),
              },
              {
                key: "amount_usd",
                label: "USD Value",
                render: (r) => formatUSD(r.amount_usd || 0),
              },
              {
                key: "status",
                label: "Status",
                render: (r) => statusBadge(r.status),
              },
              {
                key: "created_at",
                label: "Date",
                render: (r) => (
                  <span className="text-gray-400 text-xs">
                    {formatDate(r.created_at)}
                  </span>
                ),
              },
            ]}
            data={transactions}
            keyExtractor={(r) => r.id}
            emptyMessage="No deposit history yet"
          />
        </div>
      )}
    </div>
  );
};

export default Deposit;
